import React, { useState, useRef, useEffect, useContext } from "react";
import { Navbar, Nav, Dropdown, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FaBell, FaEnvelope, FaCog, FaSignOutAlt, FaUser } from "react-icons/fa";
import ProfileImage from "../../../components/ProfileImage";
import Profile from "../../../components/Profile";
import { AuthContext } from "../../../context/AuthContext";
import "../styles/Navbar.css"; // Make sure to include this CSS file

const AppNavbar = ({ notifications = [], messages = [], pageTitle }) => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  // State for dropdowns and modal
  const [showDropdown, setShowDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // Reference to the dropdown container
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };
  
  // Toggle dropdown visibility
  const toggleDropdown = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  // Handle profile click
  const handleProfileClick = () => {
    setShowDropdown(false);
    setShowProfileModal(true);
  };

  // Get user role text
  const getUserType = () => {
    if (!user) return "User";
    switch(user.userType) {
      case 0: return "Administrator";
      case 1: return "Normal User";
      case 2: return "Manager";
     
      default: return "User";
    }
  };

  // Get first 3 positions
  const getPositions = () => {
    if (!user?.positions) return [];
    return user.positions.slice(0, 3).map(pos => pos.positionTitle);
  };
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);
  
  return (
    <>
      <Navbar expand="lg" className="shadow px-3 custom-navbar">
        <div className="page-title-section">
          <h1 className="page-title">{pageTitle}</h1>
        </div>

        <div className="ms-auto d-flex align-items-center">
          {/* Email Inbox Icon */}
          <Nav.Link onClick={() => navigate("/inbox")} className="nav-icon">
            <FaEnvelope size={20} />
            {messages.length > 0 && <span className="badge-icon">{messages.length}</span>}
          </Nav.Link>

          {/* Notifications Icon */}
          <Nav.Link className="nav-icon">
            <FaBell size={20} />
            {notifications.length > 0 && <span className="badge-icon">{notifications.length}</span>}
          </Nav.Link>

          {/* Profile Dropdown */}
          <div ref={dropdownRef}>
            <Dropdown 
              align="end"
              show={showDropdown}
              className="profile-dropdown-container"
            >
              <Dropdown.Toggle 
                variant="light" 
                id="dropdown-basic" 
                className="profile-toggle"
                onClick={toggleDropdown}
              >
                <ProfileImage
                  imageUrl={user?.imageUrl || ""}
                  width={36}
                  height={36}
                />
              </Dropdown.Toggle>
              <Dropdown.Menu className="profile-dropdown">
                <div className="profile-section">
                  <ProfileImage
                    imageUrl={user?.imageUrl || ""}
                    width={50}
                    height={50}
                  />
                  <div>
                    <h6>{user?.username || "User"}</h6>
                    <p className="user-role">{getUserType()}</p>
                    <div className="user-positions">
                      {getPositions().map((position, index) => (
                        <p key={index} className="position-title">{position}</p>
                      ))}
                    </div>
                  </div>
                </div>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleProfileClick}>
                  <FaUser className="dropdown-icon" /> Profile
                </Dropdown.Item>
                <Dropdown.Item onClick={() => navigate("/settings")}>
                  <FaCog className="dropdown-icon" /> Settings
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleLogout}>
                  <FaSignOutAlt className="dropdown-icon" /> Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </Navbar>

      {/* Profile Modal */}
      <Modal 
        show={showProfileModal} 
        onHide={() => setShowProfileModal(false)}
        size="xl"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Profile Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Profile isModal={true} onClose={() => setShowProfileModal(false)} />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AppNavbar;
